import { useQuery } from '@tanstack/react-query';
import { apiCall } from '@/lib/api-client';

/**
 * F5-R6a — «Ficha de agente» (dibujo del CEO, Monitoreo v2).
 * Tipos espejo de AgentCardView (api/src/agents/agent-metrics.service.ts).
 *
 * GET /admin/agents/:id/card — solo lectura; el server AUDITA la apertura
 * (los nombres de miembros son PHI). El CIN llega YA enmascarado.
 */

export interface AgentCardPeriod {
  screenings: number;
  attempts: number;
  contactsAchieved: number;
  contactsDenominator: number;
  missedBookings: number;
  callTimeSeconds: number;
  activeWorkSeconds: number;
  ineligibleClosed: number;
}

export interface AgentCardScreening {
  id: string;
  submittedAt: string | null;
  memberName: string;
  /** «****1234» — enmascarado en el server, nunca completo. */
  cinMasked: string | null;
  isHousehold: boolean;
  /** Enum castellano de household_members.relationship_type («hijo», «madre»…). */
  householdRelation: string | null;
  householdOf: string | null;
  positiveDomains: number;
  totalDurationSec: number | null;
  findhelpStatus: 'uploaded' | 'pending' | 'flagged';
  flagReason: string | null;
  flagReasonText: string | null;
  legacyFlagText: string | null;
  notesCount: number;
  notes: Array<{ domain: string; text: string }>;
}

export interface AgentCardView {
  identity: {
    id: string;
    fullName: string;
    email: string;
    role: string;
    languages: string[];
    active: boolean;
    deactivatedAt: string | null;
  };
  live: {
    state: 'call' | 'idle' | 'available' | 'off';
    rawState: string | null;
    lastHeartbeatAt: string | null;
    loginAt: string | null;
    pageUrl: string | null;
    tabFocused: boolean | null;
    currentScreeningId: string | null;
    currentScreeningStartedAt: string | null;
    currentScreeningSeconds: number | null;
    currentAssignmentId: string | null;
    currentClientName: string | null;
    domainsAnswered: number | null;
  };
  periods: {
    today: AgentCardPeriod;
    week: AgentCardPeriod;
    month: AgentCardPeriod;
    total: AgentCardPeriod;
    weekStartNy: string;
    monthStartNy: string;
    firstActivityAt: string | null;
  };
  recent: AgentCardScreening[];
  logTotal: number;
}

export const ADMIN_AGENT_CARD_KEY = (id: string) => ['admin', 'agents', id, 'card'] as const;

export function useAgentCard(agentId: string | null) {
  return useQuery({
    queryKey: ADMIN_AGENT_CARD_KEY(agentId ?? ''),
    queryFn: () => apiCall<AgentCardView>(`/admin/agents/${agentId}/card`),
    enabled: !!agentId,
    // El estado vivo del encabezado se refresca solo; el audit del server es
    // «una fila por vista» (10 min), así que el poll no llena el audit log.
    refetchInterval: 15_000,
    refetchOnWindowFocus: false,
  });
}
